import { useState, useEffect, createContext, useContext, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Token storage key
const TOKEN_KEY = "aifilm_session_token";

// Helper functions for token management
const getStoredToken = () => localStorage.getItem(TOKEN_KEY);
const setStoredToken = (token) => {
  if (token) {
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    localStorage.removeItem(TOKEN_KEY);
  }
};
const clearStoredToken = () => localStorage.removeItem(TOKEN_KEY);

// Setup Axios interceptor to add Authorization header
axios.interceptors.request.use(
  (config) => {
    const token = getStoredToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
      // If auth fails, clear any stored token
      clearStoredToken();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Refresh user data (call after profile updates)
  const refreshUser = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
      });
      setUser(response.data);
    } catch (error) {
      console.error("Failed to refresh user:", error);
    }
  }, []);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API}/auth/login`,
        { email, password },
        { 
          withCredentials: true,
          timeout: 30000, // 30 second timeout for slow mobile connections
        }
      );
      
      // Store token in localStorage for mobile fallback
      if (response.data.token) {
        setStoredToken(response.data.token);
      }
      
      setUser(response.data);
      return response.data;
    } catch (error) {
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('Connection timeout. Please check your internet and try again.');
      }
      if (!error.response) {
        throw new Error('Network error. Please check your internet connection.');
      }
      throw error;
    }
  };

  const register = async (email, password, name) => {
    try {
      const response = await axios.post(
        `${API}/auth/register`,
        { email, password, name },
        { 
          withCredentials: true,
          timeout: 30000, // 30 second timeout for slow mobile connections
        }
      );
      
      // Store token in localStorage for mobile fallback
      if (response.data.token) {
        setStoredToken(response.data.token);
      }
      
      setUser(response.data);
      return response.data;
    } catch (error) {
      // Re-throw with better error info
      if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
        throw new Error('Connection timeout. Please check your internet and try again.');
      }
      if (!error.response) {
        throw new Error('Network error. Please check your internet connection.');
      }
      throw error;
    }
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Use current origin for Google OAuth to ensure redirect URI matches Google Console
    window.location.href = `${window.location.origin}/api/auth/google/login`;
  };

  const processGoogleCallback = async (authToken) => {
    // New flow: token comes from URL after Google OAuth callback
    if (authToken) {
      setStoredToken(authToken);
      // Fetch user data with the new token
      const response = await axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setUser(response.data);
      return response.data;
    }
    
    // Legacy Emergent Auth flow (for backwards compatibility)
    const response = await axios.post(
      `${API}/auth/session`,
      { session_id: authToken },
      { withCredentials: true }
    );
    
    // Store token in localStorage for mobile fallback
    if (response.data.token) {
      setStoredToken(response.data.token);
    }
    
    setUser(response.data);
    return response.data;
  };

  const resendVerification = async () => {
    try {
      const response = await axios.post(`${API}/auth/resend-verification`, {}, { withCredentials: true });
      return response.data;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Clear token from localStorage
    clearStoredToken();
    setUser(null);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        register,
        loginWithGoogle,
        processGoogleCallback,
        logout,
        checkAuth,
        refreshUser,
        resendVerification,
        isVerified: user?.email_verified ?? false,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
